import { router } from "@inertiajs/react"
import { ChevronDown, ChevronRight } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Radio } from "@/components/ui/radio"
import { EM_DASH, money, pct, rate } from "@/lib/money"
import { cn } from "@/lib/utils"
import type { MatrixCategoryWire, MatrixRow, MatrixSelection } from "@/types/bids"

/** One collapsible category of the full matrix: rows = the snapshot's
 *  rooms/surfaces, columns = the category's levels, cells = server-extended
 *  prices. The Base radio is document-wide per row (name = the row), so
 *  picking a level here silently unpicks any other category — exactly the
 *  cross-category exclusivity a base bid means. */
export function MatrixCategorySection({
  category,
  rows,
  selections,
  planId,
  wasteIn,
  toggleBaseUrl,
  collapsed,
  onToggleCollapsed,
}: {
  category: MatrixCategoryWire
  rows: MatrixRow[]
  selections: MatrixSelection[]
  planId: number
  wasteIn: boolean
  toggleBaseUrl: string
  collapsed: boolean
  onToggleCollapsed: () => void
}) {
  const selectionByClient = new Map(selections.map((s) => [s.client_id, s]))

  const pick = (row: MatrixRow, levelId: number) =>
    router.post(
      toggleBaseUrl,
      { bid_selection: { plan_id: planId, client_id: row.client_id, bid_level_id: levelId } },
      { preserveScroll: true, preserveState: true }
    )

  const unpick = (selection: MatrixSelection) =>
    router.delete(`${toggleBaseUrl}/${selection.id}`, { preserveScroll: true, preserveState: true })

  return (
    <section
      className="rounded-lg border border-hairline bg-surface"
      data-testid={`matrix-section-${category.key}`}
    >
      <button
        type="button"
        onClick={onToggleCollapsed}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-ink-display"
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {category.label}
        <span className="font-normal text-ink-muted">
          · {category.levels.length} level{category.levels.length === 1 ? "" : "s"} · waste{" "}
          {pct(category.waste_pct)}
          {wasteIn && " in quantities"}
        </span>
      </button>

      {!collapsed && (
        <div className="overflow-x-auto border-t border-hairline">
          <table
            className="w-full min-w-[720px] border-collapse text-sm"
            data-testid={`matrix-table-${category.key}`}
          >
            <thead>
              <tr className="border-b border-hairline text-left align-bottom text-xs uppercase tracking-wide text-ink-muted">
                <th className="px-3 pb-1.5 pt-2 font-semibold">Room</th>
                <th className="px-3 pb-1.5 pt-2 text-right font-semibold">Qty</th>
                {category.levels.map((level) => (
                  <th key={level.id} className="px-3 pb-1.5 pt-2 text-right font-semibold">
                    {level.name}
                    <span className="block font-normal normal-case">
                      {rate(level.rate, level.unit_label)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const selection = selectionByClient.get(row.client_id)
                const cells = category.cells[row.client_id] ?? {}
                return (
                  <tr key={row.client_id} className="border-b border-hairline" data-testid={`matrix-row-${category.key}-${row.client_id}`}>
                    <td className="px-3 py-2">
                      <span className="text-ink-display">{row.name}</span>
                      {row.multiplier > 1 && (
                        <span className="ml-1 text-ink-muted">×{row.multiplier}</span>
                      )}
                      {row.kind === "surface" && (
                        <Badge tone="muted" className="ml-1.5">
                          surface
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-muted">
                      {row.qty == null ? (
                        <Badge tone="signal">not measured</Badge>
                      ) : (
                        // The qty THIS category prices (waste-inflated for
                        // waste-in builders) — every cell multiplies it.
                        `${category.qtys[row.client_id] ?? row.qty} ${row.unit}`
                      )}
                    </td>
                    {category.levels.map((level) => {
                      const amount = cells[String(level.id)] ?? null
                      const checked = selection?.bid_level_id === level.id
                      return (
                        <td
                          key={level.id}
                          className={cn(
                            "px-3 py-2 text-right tabular-nums",
                            checked && "bg-accent-faded font-medium text-ink-display"
                          )}
                          data-testid={`matrix-cell-${row.client_id}-${level.id}`}
                        >
                          {amount == null ? (
                            EM_DASH
                          ) : (
                            <label className="inline-flex cursor-pointer items-center justify-end gap-2 font-normal text-inherit">
                              <Radio
                                name={`base-${planId}-${row.client_id}`}
                                checked={checked}
                                onChange={() => pick(row, level.id)}
                                onClick={() => {
                                  if (checked && selection) unpick(selection)
                                }}
                                aria-label={`${row.name} in base bid at ${level.name}`}
                                data-testid={`base-toggle-${category.key}-${row.client_id}-${level.id}`}
                              />
                              {money(amount)}
                            </label>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
            {category.base_totals && (
              <tfoot>
                <tr
                  className="border-t border-ink-muted text-sm font-semibold"
                  data-testid={`matrix-totals-${category.key}`}
                >
                  <td className="px-3 py-2" colSpan={2}>
                    Base ✓ rows at each level
                  </td>
                  {category.levels.map((level) => (
                    <td key={level.id} className="px-3 py-2 text-right tabular-nums">
                      {money(category.base_totals?.[String(level.id)] ?? null)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </section>
  )
}
